import { useEffect, useState, useMemo } from 'react';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import { CalendarDays, ChevronLeft, ChevronRight, Home, Globe, CalendarPlus, ChevronDown, Cake } from 'lucide-react';
import { cn } from '@/lib/utils';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Compact leave-type label for the calendar. Type names often carry their
// WFH and Remote reach this calendar two ways: as a work-location row (legacy
// WorkSchedule entries, keyed by `type`) or as a leave booking (keyed by
// `typeId`). They mean the same thing to whoever is reading the calendar, so
// both get the same icon.
function iconForType(id) {
  if (id === 'wfh') return 'home';
  if (id === 'remote') return 'globe';
  return null;
}

// abbreviation in a trailing parenthesis (e.g. "Work From Home (WFH)"); the
// calendar is space-constrained, so show just the abbreviation when present
// ("WFH") and fall back to the full name otherwise ("Annual", "Sick").
function shortLeaveLabel(name) {
  if (!name) return name;
  const m = String(name).match(/\(([^)]+)\)\s*$/);
  return m ? m[1] : name;
}

export default function TeamCalendarPage() {
  const toast = useToast();
  const [monthOffset, setMonthOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'leave' | 'birthday' | a work-location id

  // Pin to day 1 BEFORE applying monthOffset — see PlannerPage for the
  // same fix. setMonth() inherits the current day, so navigating from
  // e.g. "Apr 30" to a short month overflows ("Feb 30" → "Mar 2") and
  // the short month gets silently skipped.
  const { year, month, monthLabel, daysInMonth, startDay } = useMemo(() => {
    const today = new Date();
    const d = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    return {
      year: d.getFullYear(),
      month: d.getMonth(),
      monthLabel: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      daysInMonth: new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(),
      startDay: new Date(d.getFullYear(), d.getMonth(), 1).getDay(),
    };
  }, [monthOffset]);

  useEffect(() => {
    setLoading(true);
    const ms = String(month + 1).padStart(2, '0');
    const sd = `${year}-${ms}-01`;
    const ed = `${year}-${ms}-${String(daysInMonth).padStart(2, '0')}`;

    employeeApi.getTeamCalendar(sd, ed)
      .then((res) => setData(res))
      .catch((err) => {
        toast.error(`Failed to load team calendar: ${err.message}`);
        setData({ leave: [], schedule: [], holidays: [], birthdays: [] });
      })
      .finally(() => setLoading(false));
  }, [year, month, daysInMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const holidayMap = useMemo(() => {
    const m = {};
    (data?.holidays || []).forEach((h) => { m[h.date] = h.name; });
    return m;
  }, [data?.holidays]);

  // Build day map: day number -> array of items for that day
  const dayMap = useMemo(() => {
    if (!data) return {};
    const map = {};
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const items = [];

      // Holidays
      if (holidayMap[ds]) {
        items.push({ type: 'holiday', label: holidayMap[ds], color: '#F85707' });
      }

      // Birthdays — the server sends the day + month for the viewed year only
      // (never the birth year). Listed before leave so a birthday is not the
      // thing pushed under "+N more" on a busy day.
      for (const b of data.birthdays || []) {
        if (b.date === ds) {
          if (filter !== 'all' && filter !== 'birthday') continue;
          items.push({
            type: 'birthday',
            label: b.userName?.split(' ')[0] || 'Employee',
            sublabel: 'Birthday',
            color: '#FC026F',
            icon: 'cake',
          });
        }
      }

      // Leave (approved shows solid, pending shows with indicator)
      for (const l of data.leave || []) {
        if (l.startDate <= ds && l.endDate >= ds) {
          if (filter !== 'all' && filter !== 'leave') continue;
          items.push({
            type: 'leave',
            label: l.userName?.split(' ')[0] || 'Employee',
            sublabel: shortLeaveLabel(l.typeName),
            color: l.typeColor || '#3985FD',
            status: l.status,
            icon: iconForType(l.typeId),
          });
        }
      }

      // WFH / Remote
      for (const s of data.schedule || []) {
        if (s.date === ds) {
          if (filter !== 'all' && filter !== s.type) continue;
          items.push({
            type: s.type,
            label: s.userName?.split(' ')[0] || 'Employee',
            color: s.color || '#64748b',
            icon: iconForType(s.type),
          });
        }
      }

      map[d] = items;
    }
    return map;
  }, [data, daysInMonth, year, month, holidayMap, filter]);

  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDate = today.getDate();

  const [hoveredDay, setHoveredDay] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0, side: 'bottom' });
  const [showAddToOutlook, setShowAddToOutlook] = useState(false);

  const mailbox = data?.notificationFromEmail || '';

  const handleDayEnter = (day, e) => {
    const items = dayMap[day] || [];
    if (items.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const popoverHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const side = spaceBelow >= popoverHeight + 16 ? 'bottom' : 'top';
    const popoverWidth = 320;
    let x = rect.left + rect.width / 2;
    const halfW = popoverWidth / 2;
    x = Math.max(halfW + 12, Math.min(window.innerWidth - halfW - 12, x));
    const y = side === 'bottom' ? rect.bottom + 8 : rect.top - 8;
    setHoverPos({ x, y, side });
    setHoveredDay(day);
  };

  const handleDayLeave = () => setHoveredDay(null);

  const hoveredItems = hoveredDay ? (dayMap[hoveredDay] || []) : [];
  const hoveredDateStr = hoveredDay
    ? `${year}-${String(month + 1).padStart(2, '0')}-${String(hoveredDay).padStart(2, '0')}`
    : null;
  const hoveredDayName = hoveredDay
    ? FULL_DAY_NAMES[new Date(year, month, hoveredDay).getDay()]
    : null;
  const hoveredHoliday = hoveredDateStr ? holidayMap[hoveredDateStr] : null;

  const leaveCount = (data?.leave || []).filter((l) => l.status === 'approved').length;
  const pendingCount = (data?.leave || []).filter((l) => l.status === 'pending').length;
  const birthdayCount = (data?.birthdays || []).length;
  const scheduleCountByType = (data?.schedule || []).reduce((m, s) => { m[s.type] = (m[s.type] || 0) + 1; return m; }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Team Calendar</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            See who's on leave, working from home, remote, or celebrating a birthday across the team
          </p>
        </div>
        <div className="relative shrink-0">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowAddToOutlook((s) => !s)}
            className="h-9"
          >
            <CalendarPlus className="h-3.5 w-3.5" />
            Add to Outlook
            <ChevronDown className={cn('h-3 w-3 transition-transform', showAddToOutlook && 'rotate-180')} />
          </Button>
          {showAddToOutlook && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setShowAddToOutlook(false)} />
              <div className="absolute right-0 top-full z-40 mt-1 w-[420px] rounded-lg border border-border bg-card p-4 shadow-lg space-y-3">
                <div className="flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">Add the team calendar to Outlook</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Every approved leave, WFH, and Remote day is written to the HR notification
                  mailbox's calendar in real time. Add that mailbox as a shared calendar in
                  Outlook to see the same view there — adds, edits, and removals all sync within
                  seconds.
                </p>

                {mailbox ? (
                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Calendar to add
                    </div>
                    <div className="font-mono text-sm text-foreground select-all break-all">{mailbox}</div>
                  </div>
                ) : (
                  <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
                    Notification mailbox isn't configured yet — ask your admin to set it in Settings.
                  </div>
                )}

                <div className="text-xs">
                  <div className="font-medium text-foreground mb-1">Outlook desktop (Windows)</div>
                  <ol className="list-decimal list-inside text-muted-foreground space-y-0.5 ml-1">
                    <li>Switch to <strong>Calendar</strong> view (bottom-left icon).</li>
                    <li>Right-click <strong>My Calendars</strong> → <strong>Add Calendar</strong> → <strong>Add from Directory</strong>.</li>
                    <li>Search {mailbox ? <code className="text-foreground">{mailbox}</code> : 'the mailbox email above'}, pick it, click <strong>OK</strong>.</li>
                    <li>Tick the new shared calendar to overlay it on yours.</li>
                  </ol>
                </div>

                <div className="text-xs">
                  <div className="font-medium text-foreground mb-1">Outlook on the web</div>
                  <ol className="list-decimal list-inside text-muted-foreground space-y-0.5 ml-1">
                    <li>
                      Open{' '}
                      <a
                        href="https://outlook.office.com/calendar/"
                        target="_blank"
                        rel="noopener"
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        outlook.office.com/calendar
                      </a>
                      .
                    </li>
                    <li><strong>Add calendar</strong> (left pane) → <strong>Add from directory</strong>.</li>
                    <li>Search {mailbox ? <code className="text-foreground">{mailbox}</code> : 'the mailbox email above'} → pick it → <strong>People's calendars</strong> → <strong>Add</strong>.</li>
                    <li>Tick its checkbox to overlay it.</li>
                  </ol>
                </div>

                <div className="pt-1 border-t border-border">
                  <p className="text-[11px] text-muted-foreground">
                    <strong className="text-foreground">For Teams meetings:</strong> no setup needed.
                    Each approved leave also adds an out-of-office event to that person's own
                    Outlook, so Scheduling Assistant flags conflicts automatically.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">{monthLabel}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setMonthOffset((o) => o - 1)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setMonthOffset(0)} className="rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground">Today</button>
            <button onClick={() => setMonthOffset((o) => o + 1)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              <span className="ml-3">Loading...</span>
            </div>
          ) : (
            <>
              {/* Legend + filter */}
              <div className="mb-4 flex items-center gap-3 flex-wrap">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'leave', label: `Leave (${leaveCount}${pendingCount ? ` + ${pendingCount} pending` : ''})` },
                  { id: 'birthday', label: `Birthdays (${birthdayCount})` },
                  ...(data?.workLocations || []).filter((w) => w.active).map((w) => ({
                    id: w.id,
                    label: `${w.label} (${scheduleCountByType[w.id] || 0})`,
                  })),
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={cn(
                      'rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors',
                      filter === f.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:bg-muted/50'
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Grid */}
              <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-border bg-border">
                {DAY_NAMES.map((d) => (
                  <div key={d} className="bg-muted/50 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>
                ))}
                {Array.from({ length: startDay }).map((_, i) => <div key={`e-${i}`} className="min-h-[80px] bg-card/30" />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const items = dayMap[day] || [];
                  const isToday = isCurrentMonth && day === todayDate;
                  // Weekend = anything that isn't a company working day.
                  // The backend returns the configured workingDays array
                  // (defaults [0..4] for KSA Sun-Thu, so weekend columns
                  // are Fri (5) + Sat (6)).
                  const dow = (startDay + i) % 7;
                  const workingDaySet = data?.workingDays || [0, 1, 2, 3, 4];
                  const isWeekend = !workingDaySet.includes(dow);

                  return (
                    <div
                      key={day}
                      onMouseEnter={(e) => handleDayEnter(day, e)}
                      onMouseLeave={handleDayLeave}
                      className={cn(
                        'min-h-[80px] p-1.5',
                        isWeekend ? 'bg-card/30' : 'bg-card',
                        isToday && 'ring-2 ring-inset ring-primary',
                        items.length > 0 && 'cursor-default',
                      )}
                    >
                      <div className={cn('mb-1 text-[11px] font-medium', isToday ? 'text-primary font-bold' : 'text-muted-foreground')}>
                        {day}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        {items.slice(0, 4).map((item, ei) => (
                          <div
                            key={ei}
                            className="truncate rounded px-1 py-px text-[9px] font-medium flex items-center gap-0.5"
                            style={{
                              background: item.status === 'pending' ? 'rgba(180,83,9,0.1)' : item.color + '18',
                              color: item.status === 'pending' ? '#B45309' : item.color,
                            }}
                            title={item.sublabel ? `${item.label} — ${item.sublabel}${item.status === 'pending' ? ' (pending)' : ''}` : item.label}
                          >
                            {item.icon === 'home' && <Home className="h-2 w-2 shrink-0" />}
                            {item.icon === 'globe' && <Globe className="h-2 w-2 shrink-0" />}
                            {item.icon === 'cake' && <Cake className="h-2 w-2 shrink-0" />}
                            {item.label}
                            {item.status === 'pending' && <span className="opacity-60 ml-0.5">*</span>}
                          </div>
                        ))}
                        {items.length > 4 && (
                          <div className="px-1 text-[9px] text-muted-foreground">+{items.length - 4} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {hoveredDay && hoveredItems.length > 0 && (
        <div
          className="pointer-events-none fixed z-50 w-80 rounded-lg border border-border bg-popover p-4 shadow-2xl"
          style={{
            left: hoverPos.x,
            top: hoverPos.y,
            transform: hoverPos.side === 'bottom' ? 'translateX(-50%)' : 'translate(-50%, -100%)',
          }}
        >
          <div className="mb-3 flex items-baseline justify-between border-b border-border pb-2">
            <div>
              <div className="text-lg font-bold text-foreground leading-none">
                {hoveredDayName}, {new Date(year, month, hoveredDay).toLocaleString('en-US', { month: 'short' })} {hoveredDay}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                {hoveredItems.length} {hoveredItems.length === 1 ? 'event' : 'events'}
              </div>
            </div>
            {isCurrentMonth && hoveredDay === todayDate && (
              <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">Today</span>
            )}
          </div>

          {hoveredHoliday && (
            <div
              className="mb-2 rounded-md px-2 py-1.5 text-xs font-semibold"
              style={{ background: '#F8570718', color: '#F85707' }}
            >
              {hoveredHoliday}
            </div>
          )}

          <div className="max-h-[260px] space-y-1 overflow-y-auto">
            {hoveredItems.filter((it) => it.type !== 'holiday').map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs"
                style={{
                  background: item.status === 'pending' ? 'rgba(180,83,9,0.1)' : item.color + '18',
                  color: item.status === 'pending' ? '#B45309' : item.color,
                }}
              >
                {item.icon === 'home' && <Home className="h-3 w-3 shrink-0" />}
                {item.icon === 'globe' && <Globe className="h-3 w-3 shrink-0" />}
                {item.icon === 'cake' && <Cake className="h-3 w-3 shrink-0" />}
                <span className="font-semibold">{item.label}</span>
                {item.sublabel && <span className="opacity-80">— {item.sublabel}</span>}
                {item.status === 'pending' && (
                  <span className="ml-auto rounded bg-[#B45309]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">
                    Pending
                  </span>
                )}
                {item.type === 'wfh' && !item.status && (
                  <span className="ml-auto text-[9px] uppercase tracking-wide opacity-60">WFH</span>
                )}
                {item.type === 'remote' && !item.status && (
                  <span className="ml-auto text-[9px] uppercase tracking-wide opacity-60">Remote</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
